/**
 * R5.7 — Document Reader V3: HTML primary, title-only top, thumb-friendly bottom Back.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

export type DocumentViewModelClient = {
  document_id: string;
  document_number: string;
  type?: string;
  type_title_uk: string;
  version: number;
  order_public_number: string;
  issued_at_uk: string;
  trade_name: string;
  slogan: string;
  seller: {
    legal_person_line: string;
    legal_name_line: string;
    tax_id: string;
    address: string;
    bank_name: string;
    iban_display: string;
  };
  buyer_lines: string[];
  payment_purpose: string | null;
  delivery_lines: string[];
  line_items: Array<{
    title: string;
    qty: number;
    unit: string;
    unit_price_uah: string;
    line_total_uah: string;
  }>;
  subtotal_uah: string;
  total_uah: string;
  amount_in_words_uk: string;
  responsible_person_uk: string;
  signature_line_uk: string;
};

export type DocumentReaderShellProps = {
  lang: "uk" | "en" | "ua";
  view: DocumentViewModelClient | null;
  loading?: boolean;
  error?: string | null;
  onBack: () => void;
  onDownloadPdf: () => void;
  onOpenPdfMode: () => void;
  onCopyNumber?: () => void;
  onOpenExternal?: () => void;
  testIdPrefix?: string;
};

function tgVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  try {
    const tg = (window as unknown as { Telegram?: { WebApp?: { themeParams?: Record<string, string> } } })
      .Telegram?.WebApp;
    const v = tg?.themeParams?.[name];
    if (v && typeof v === "string" && v.trim()) return v.trim();
  } catch {
    /* ignore */
  }
  return fallback;
}

/** Prefer branded green when Telegram theme would yield low-contrast actions. */
function actionColors(): { bg: string; fg: string } {
  const bg = tgVar("button_color", "#1f3d2a");
  const fg = tgVar("button_text_color", "#ffffff");
  // Guard: if button bg is too light, force dark brand.
  const hex = bg.replace("#", "");
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (lum > 0.72) return { bg: "#1f3d2a", fg: "#ffffff" };
  }
  return { bg, fg };
}

function Section({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <section style={sectionCard} data-testid={testId}>
      <h3 style={sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

export function DocumentNativeReader(props: DocumentReaderShellProps) {
  const en = props.lang === "en";
  const v = props.view;
  const prefix = props.testIdPrefix ?? "doc-reader";
  const [menuOpen, setMenuOpen] = useState(false);
  const actions = actionColors();
  const pageBg = tgVar("secondary_bg_color", "#f2f3f5");
  const textColor = tgVar("text_color", "#0f1115");
  const hintColor = tgVar("hint_color", "#6b7280");
  const cardBg = tgVar("bg_color", "#ffffff");

  const shell: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 90,
    background: pageBg,
    display: "flex",
    flexDirection: "column",
    color: textColor,
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  };

  return (
    <div style={shell} data-testid={`${prefix}-native`} data-reader-mode="responsive-html-v3">
      {/* Top: title + overflow only (no Back — Back lives in bottom thumb bar) */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingTop: "calc(10px + env(safe-area-inset-top, 0px))",
          paddingBottom: 10,
          paddingLeft: 14,
          paddingRight: 12,
          background: cardBg,
          color: textColor,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
        data-testid={`${prefix}-header`}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {v?.type_title_uk ?? (en ? "Document" : "Документ")}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMenuOpen((x) => !x)}
          data-testid={`${prefix}-more`}
          aria-label="More"
          style={{
            border: `1.5px solid ${actions.bg}`,
            background: cardBg,
            color: actions.bg,
            borderRadius: 10,
            minHeight: 44,
            minWidth: 44,
            fontSize: 20,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          ⋯
        </button>
      </header>

      {menuOpen ? (
        <div
          style={{
            position: "absolute",
            right: 12,
            top: "calc(56px + env(safe-area-inset-top, 0px))",
            zIndex: 3,
            background: cardBg,
            color: textColor,
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 12,
            padding: 8,
            minWidth: 220,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
          data-testid={`${prefix}-overflow`}
        >
          <MenuBtn
            testId={`${prefix}-pdf-mode`}
            label={en ? "PDF version" : "PDF-версія"}
            onClick={() => {
              setMenuOpen(false);
              props.onOpenPdfMode();
            }}
          />
          <MenuBtn
            testId={`${prefix}-menu-download`}
            label={en ? "Download PDF" : "Завантажити PDF"}
            onClick={() => {
              setMenuOpen(false);
              props.onDownloadPdf();
            }}
          />
          {props.onCopyNumber ? (
            <MenuBtn
              testId={`${prefix}-copy-number`}
              label={en ? "Copy document number" : "Копіювати номер документа"}
              onClick={() => {
                setMenuOpen(false);
                props.onCopyNumber?.();
              }}
            />
          ) : null}
          {props.onOpenExternal ? (
            <MenuBtn
              testId={`${prefix}-external`}
              label={en ? "Open in browser (fallback)" : "Відкрити у браузері (запасний)"}
              onClick={() => {
                setMenuOpen(false);
                props.onOpenExternal?.();
              }}
            />
          ) : null}
        </div>
      ) : null}

      <main
        style={{
          flex: 1,
          overflow: "auto",
          padding: "16px 14px 12px",
          fontSize: 16,
          lineHeight: 1.5,
          paddingBottom: 8,
        }}
      >
        {props.loading ? <p data-testid={`${prefix}-loading`}>{en ? "Loading…" : "Завантаження…"}</p> : null}
        {props.error ? (
          <p data-testid={`${prefix}-error`} style={{ color: "#b91c1c", fontSize: 16 }}>
            {props.error}
          </p>
        ) : null}
        {v ? (
          <article data-testid={`${prefix}-body`} data-not-pdf-canvas="1">
            <p style={{ margin: "0 0 2px", fontSize: 14, color: hintColor }}>№ {v.document_number}</p>
            <p style={{ margin: "0 0 16px", fontSize: 14, color: hintColor }}>{v.issued_at_uk}</p>

            <div style={{ marginBottom: 18 }}>
              <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 800 }}>{v.trade_name}</h2>
              <p style={{ margin: 0, fontSize: 14, color: hintColor }}>{v.slogan}</p>
            </div>

            <Section title={en ? "Supplier" : "Постачальник"} testId={`${prefix}-seller`}>
              <Line>{v.seller.legal_person_line}</Line>
              <Line strong>{v.seller.legal_name_line}</Line>
              <Line muted>РНОКПП: {v.seller.tax_id}</Line>
              <Line muted>{v.seller.address}</Line>
              <Line muted>{v.seller.bank_name}</Line>
              <Line muted>IBAN: {v.seller.iban_display}</Line>
            </Section>

            <Section title={en ? "Buyer" : "Покупець"} testId={`${prefix}-buyer`}>
              {v.buyer_lines.map((line) => (
                <Line key={line}>{line}</Line>
              ))}
            </Section>

            <Section title={en ? "Order" : "Замовлення"} testId={`${prefix}-order`}>
              <Line strong>{v.order_public_number}</Line>
            </Section>

            {v.payment_purpose ? (
              <Section title={en ? "Payment purpose" : "Призначення платежу"} testId={`${prefix}-payment`}>
                <Line>{v.payment_purpose}</Line>
              </Section>
            ) : null}

            {v.delivery_lines.length > 0 ? (
              <Section title={en ? "Delivery" : "Доставка"} testId={`${prefix}-delivery`}>
                {v.delivery_lines.map((line) => (
                  <Line key={line}>{line}</Line>
                ))}
              </Section>
            ) : null}

            <Section title={en ? "Goods" : "Товари"} testId={`${prefix}-items`}>
              {v.line_items.map((item, idx) => (
                <div
                  key={`${item.title}-${idx}`}
                  style={{
                    borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.08)",
                    padding: "12px 0",
                  }}
                  data-testid={`${prefix}-item-${idx}`}
                >
                  <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.35, wordBreak: "break-word" }}>
                    {item.title}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 15, color: hintColor }}>
                    {en ? "Qty" : "Кількість"}: {item.qty} {item.unit}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 15, color: hintColor }}>
                    {en ? "Price" : "Ціна"}: {item.unit_price_uah} грн
                  </div>
                  <div style={{ marginTop: 4, fontSize: 16, fontWeight: 700 }}>
                    {en ? "Amount" : "Сума"}: {item.line_total_uah} грн
                  </div>
                </div>
              ))}
            </Section>

            <section
              style={{ ...sectionCard, border: `2px solid ${actions.bg}` }}
              data-testid={`${prefix}-totals`}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  fontWeight: 800,
                  fontSize: 20,
                }}
              >
                <span>{en ? "Total" : "Всього"}</span>
                <span>{v.total_uah} грн</span>
              </div>
              <p style={{ margin: "12px 0 0", fontSize: 15, color: hintColor, lineHeight: 1.45 }}>
                {en ? "Amount in words" : "Сума словами"}:
                <br />
                <span style={{ color: textColor, fontWeight: 600 }}>{v.amount_in_words_uk}</span>
              </p>
            </section>

            <Section title={en ? "Responsible person" : "Відповідальна особа"} testId={`${prefix}-sign`}>
              <Line>{v.responsible_person_uk}</Line>
              <Line muted>{v.signature_line_uk}</Line>
            </Section>

            <p style={{ margin: "8px 0 0", fontSize: 13, color: hintColor }}>v{v.version}</p>
          </article>
        ) : null}
      </main>

      {/* Thumb-friendly sticky bottom: Back + PDF + overflow */}
      <footer
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 12px calc(10px + env(safe-area-inset-bottom, 0px))",
          borderTop: "1px solid rgba(0,0,0,0.1)",
          background: cardBg,
          boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
        }}
        data-testid={`${prefix}-bottom-nav`}
      >
        <button
          type="button"
          onClick={props.onBack}
          data-testid={`${prefix}-back`}
          aria-label={en ? "Back" : "Назад"}
          style={{
            flex: 1.4,
            border: "none",
            background: actions.bg,
            color: actions.fg,
            borderRadius: 12,
            padding: "14px 12px",
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            minHeight: 52,
          }}
        >
          ← {en ? "Back" : "Назад"}
        </button>
        <button
          type="button"
          onClick={props.onOpenPdfMode}
          data-testid={`${prefix}-pdf-bottom`}
          style={{
            flex: 1,
            border: `2px solid ${actions.bg}`,
            background: cardBg,
            color: actions.bg,
            borderRadius: 12,
            padding: "14px 10px",
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
            minHeight: 52,
          }}
        >
          PDF
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen((x) => !x)}
          data-testid={`${prefix}-more-bottom`}
          style={{
            border: `2px solid ${actions.bg}`,
            background: cardBg,
            color: actions.bg,
            borderRadius: 12,
            minWidth: 52,
            minHeight: 52,
            fontSize: 20,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          ⋯
        </button>
      </footer>
    </div>
  );
}

function Line({
  children,
  strong,
  muted,
}: {
  children: ReactNode;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <p
      style={{
        margin: "0 0 6px",
        fontSize: muted ? 15 : 16,
        fontWeight: strong ? 700 : 400,
        opacity: muted ? 0.78 : 1,
        lineHeight: 1.45,
        wordBreak: "break-word",
      }}
    >
      {children}
    </p>
  );
}

function MenuBtn({ label, onClick, testId }: { label: string; onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        border: "none",
        background: "transparent",
        padding: "12px 10px",
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

const sectionCard: CSSProperties = {
  background: "var(--tg-theme-bg-color, #ffffff)",
  borderRadius: 14,
  padding: "14px 16px",
  marginBottom: 14,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const sectionTitle: CSSProperties = {
  margin: "0 0 10px",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.06,
  textTransform: "uppercase",
  opacity: 0.65,
};

export async function renderPdfPagesHighDpi(input: {
  httpsUrl: string;
  host: HTMLElement;
  minCssWidth?: number;
  testIdPrefix?: string;
}): Promise<number> {
  const pdfjs = await import("pdfjs-dist");
  const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const task = pdfjs.getDocument({ url: input.httpsUrl, withCredentials: false });
  const pdf = await task.promise;
  input.host.replaceChildren();
  const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
  const cssWidth = Math.max(
    input.minCssWidth ?? 300,
    input.host.clientWidth || window.innerWidth - 16,
  );
  for (let pageNo = 1; pageNo <= pdf.numPages; pageNo++) {
    const page = await pdf.getPage(pageNo);
    const base = page.getViewport({ scale: 1 });
    const cssScale = cssWidth / base.width;
    const viewport = page.getViewport({ scale: cssScale * dpr });
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(cssWidth)}px`;
    canvas.style.height = "auto";
    canvas.style.display = "block";
    canvas.style.margin = "0 0 8px";
    canvas.dataset.testid = `${input.testIdPrefix ?? "pdf-page"}-${pageNo}`;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas");
    await page.render({ canvasContext: ctx, viewport }).promise;
    input.host.appendChild(canvas);
  }
  return pdf.numPages;
}

export function usePreferNativeDocumentReader(): boolean {
  return true;
}
